import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Div, Loader, Title } from '@gaiads/telia-react-component-library';
import {
  AdminProductEnvelope,
  LocaleResponse,
  LocalProperty,
  Tag,
  ProductTranslation,
  UrlImage,
  FinanceProductId,
  AdminProductTypeEnum,
  AdminProductVariant,
  AdminSpecItem,
  Video,
} from '@cdaas/api-clients/dist/product-service-openapi';

import { RootState } from '../../store';
import { selectAllLocales, selectCurrentLocale } from '../../store/locales';
import { fetchAdminCategories, selectAllCategories } from '../../store/adminCategories';
import ProductPopulate from './ProductPopulate/ProductPopulate';
import { ProductEdit } from './ProductEdit/ProductEdit';
import { getInitialProductTranslation, getInitialSpecTranslation } from '../../utils/products';
import { fillArrayEnd } from '../../utils/entities';
import { setNotification } from '../../store/ui';
import {
  productServiceAdminProductsClient,
  productServiceAdminTagsClient,
  productServicePropertiesClient,
} from '../../api-client/product-service-client';
import { selectAllCountries } from '../../store/adminCountries';
import { TeliaFinanceGuidChangeParam } from './ProductEdit/utils';

import './ProductForm.scss';

export interface ProductFormParamTypes {
  productId?: string;
}

export default function ProductForm() {
  const productId = Number(useParams<ProductFormParamTypes>().productId);

  const { t } = useTranslation('productscreen');
  const dispatch = useDispatch();

  const locales = useSelector((state: RootState) => selectAllLocales(state));
  const countries = useSelector((state: RootState) => selectAllCountries(state));

  const [currentLocale, setCurrentLocale] = useState<LocaleResponse>(useSelector(selectCurrentLocale) || locales?.[0]);

  const allCategoriesInSystem = useSelector((state: RootState) => selectAllCategories(state));
  const [allTagsInSystem, setAllTagsInSystem] = useState<Tag[]>([]);
  const [allPropertiesInSystem, setAllPropertiesInSystem] = useState<LocalProperty[]>([]);

  const [variationsExist, setVariationsExist] = useState<boolean>(false);

  const [addMode, setAddMode] = useState<boolean>(false);
  const [populateMode, setPopulateMode] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  // Product envelope data split to smaller parts
  const [id, setId] = useState<AdminProductEnvelope['product']['id']>();
  const [type, setType] = useState<AdminProductEnvelope['product']['type']>();
  const [name, setName] = useState<AdminProductEnvelope['product']['name']>();
  const [brand, setBrand] = useState<AdminProductEnvelope['product']['brand']>();
  const [variants, setVariants] = useState<AdminProductEnvelope['product']['variants']>();
  const [subCategories, setSubCategories] = useState<AdminProductEnvelope['product']['subcategory_ids']>();
  const [tags, setTags] = useState<AdminProductEnvelope['product']['tag_ids']>();
  const [images, setImages] = useState<UrlImage[]>();
  const [keySellingPoints, setKeySellingPoints] = useState<AdminProductEnvelope['product']['key_selling_points']>();
  const [description, setDescription] = useState<AdminProductEnvelope['product']['description']>();
  const [boxContents, setBoxContents] = useState<AdminProductEnvelope['product']['box_content']>();
  const [specs, setSpecs] = useState<AdminProductEnvelope['product']['main_specs']>();
  const [videos, setVideos] = useState<AdminProductEnvelope['product']['videos']>();
  const [properties, setProperties] = useState<AdminProductEnvelope['properties']>();
  const [propertyValues, setPropertyValues] = useState<AdminProductEnvelope['property_values']>();
  // Split translations of product into even smaller parts
  // translations metadata maps localeId to translation id
  const [translationsMetadata, setTranslationsMetadata] = useState<{ id: number; localeId: number }[]>();
  const [nameTranslations, setNameTranslations] = useState<{ localeId: number; translation: string }[]>();
  const [keySellingPointsTranslations, setKeySellingPointsTranslations] = useState<{ localeId: number; translations: string[] }[]>();
  const [descriptionTranslations, setDescriptionTranslations] = useState<{ localeId: number; translation: string }[]>();
  const [boxContentsTranslations, setBoxContentsTranslations] = useState<{ localeId: number; translations: string[] }[]>();

  // Create a memoized callback for changing each part of the data
  const handleNameChange = useCallback((name: string) => {
    setName(name);
  }, []);
  const handleBrandChange = useCallback((brand: string) => {
    setBrand(brand);
  }, []);
  const handleTypeChange = useCallback(
    (type: AdminProductTypeEnum) => {
      setType(type);
      if (type === AdminProductTypeEnum.Service) {
        const updatedVariants = (variants || []).map(variant => ({
          ...variant,
          finance_product_ids: [],
        }));
        setVariants(updatedVariants);
      }
    },
    [variants],
  );

  // Set product's initial finance id's here. These are used as follows:
  // - If there's no finance id for some country yet and user chooses one -> create new finance id object for that country for each variant
  // - If there's already a finance id for some country and it's changed -> don't create new finance id object but just update the finance id fields
  const [initialFinanceProductIds, setInitialFinanceProductIds] = useState<FinanceProductId[]>([]);
  const handleVariantsTeliaFinanceGUIDChange = useCallback(
    (financeProductId: TeliaFinanceGuidChangeParam) => {
      // For now, even though data model allows each variant of a product to have different Finance id per country, currently each
      // variant of a product should have same finance id in same country
      const variantsWithUpdatedFinanceIds = (variants || []).map(variant => {
        // Remove currently chosen finance id used for the specific country
        const updatedFinanceProductIds = variant.finance_product_ids.filter(fpid => fpid.country_id !== financeProductId.country_id);
        // Possibly replace with other finance id to be used for the specific country.
        // Replacement is not mandatory, user can also just remove current finance id used in a country by only
        // providing country id param and no finance id param -> finance id in that country is removed
        if (financeProductId.finance_product_id) {
          const initial = initialFinanceProductIds.find(
            initialFinanceProductId =>
              initialFinanceProductId.country_id === financeProductId.country_id && initialFinanceProductId.variant_id === variant.id,
          );
          // Either update existing finance id object (initial found - there's already finance id object in the country)
          // or create new finance id object (setting 0 should make backend create the id based on autoincr db field or something)
          const id = initial?.id ?? 0;
          updatedFinanceProductIds.push({
            country_id: financeProductId.country_id,
            finance_product_id: financeProductId.finance_product_id,
            variant_id: variant.id,
            id,
          });
        }
        return { ...variant, finance_product_ids: updatedFinanceProductIds };
      });
      setVariants(variantsWithUpdatedFinanceIds);
    },
    [variants, initialFinanceProductIds],
  );

  const handleVariantsChange = useCallback((variants: AdminProductVariant[]) => {
    setVariants(variants);
  }, []);
  const handleSubCategoriesChange = useCallback((subCategories: number[]) => {
    setSubCategories(subCategories);
  }, []);
  const handleTagsChange = useCallback((tags: number[]) => {
    setTags(tags);
  }, []);
  const handleImagesChange = useCallback((images: UrlImage[]) => {
    setImages(images);
  }, []);
  const handleBoxContentsChange = useCallback((boxContents: string[]) => {
    setBoxContents(boxContents);
  }, []);
  const handleKeySellingPointsChange = useCallback((keySellingPoints: string[]) => {
    setKeySellingPoints(keySellingPoints);
  }, []);
  const handleDescriptionChange = useCallback((description: string) => {
    setDescription(description);
  }, []);
  const handleSpecsChange = useCallback((specs: AdminSpecItem[]) => {
    setSpecs(specs);
  }, []);
  const handleVideosChange = useCallback((videos: Video[]) => {
    setVideos(videos);
  }, []);
  const handleNameTranslationsChange = useCallback(
    (
      translations: {
        localeId: number;
        translation: string;
      }[],
    ) => {
      setNameTranslations(translations);
    },
    [],
  );
  const handleKeySellingPointsTranslationsChange = useCallback(
    (
      translations: {
        localeId: number;
        translations: string[];
      }[],
    ) => {
      setKeySellingPointsTranslations(translations);
    },
    [],
  );
  const handleDescriptionTranslationsChange = useCallback(
    (
      translations: {
        localeId: number;
        translation: string;
      }[],
    ) => {
      setDescriptionTranslations(translations);
    },
    [],
  );
  const handleBoxContentsTranslationsChange = useCallback(
    (
      translations: {
        localeId: number;
        translations: string[];
      }[],
    ) => {
      setBoxContentsTranslations(translations);
    },
    [],
  );

  const handleAddProperty = useCallback(
    (propertyToAdd: LocalProperty) => {
      setProperties([...(properties || []), propertyToAdd]);
    },
    [properties],
  );

  const handleRemoveProperty = useCallback(
    (propertyToRemove: LocalProperty) => {
      const updatedProperties = properties?.filter(property => property.id !== propertyToRemove.id);
      const updatedPropertyValues = propertyValues?.filter(propertyValue => propertyValue.property_id !== propertyToRemove.id);

      const propertyValueIdsToRemove = propertyToRemove.property_values?.map(propertyValue => propertyValue.id) ?? [];

      //Remove all property values of that property type from variants
      const updatedVariants = variants?.map(variant => {
        return {
          ...variant,
          property_value_ids: (variant.property_value_ids || []).filter(propertyValueId => !propertyValueIdsToRemove.includes(propertyValueId)),
        };
      });
      // Remove all property values of that property type from images
      const updatedImages = images?.map(image => {
        return {
          ...image,
          property_value_ids: (image.property_value_ids || []).filter(propertyValueId => !propertyValueIdsToRemove.includes(propertyValueId)),
        };
      });

      setProperties(updatedProperties);
      setPropertyValues(updatedPropertyValues);
      setVariants(updatedVariants);
      setImages(updatedImages);
    },
    [properties, propertyValues, variants, images],
  );

  const handleLocaleChange = useCallback(
    (localeId: number) => {
      const chosenLocale = locales.find(locale => locale.id === localeId);
      if (chosenLocale) {
        setCurrentLocale(chosenLocale);
      }
    },
    [locales],
  );

  const initializeProductDataFromEnvelope = useCallback(
    (productEnvelope: AdminProductEnvelope) => {
      setProperties(productEnvelope.properties);
      setPropertyValues(productEnvelope.property_values);

      const product = productEnvelope.product;
      setId(product.id);
      setType(product.type);
      setName(product.name);
      setBrand(product.brand);
      if (product.variants.length > 0) {
        setInitialFinanceProductIds(product.variants?.flatMap(variant => variant.finance_product_ids));
      }
      // Prices are not tied to product but to sales channels the product is added to
      // Updating product without prices should NOT affect the price it has on different sales channels
      const variantsWithoutPrices = product.variants?.map(variant => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { prices, ...rest } = variant;
        return rest;
      });
      setVariants(variantsWithoutPrices);
      setSubCategories(product.subcategory_ids);
      setTags(product.tag_ids);
      setImages(product.images as unknown as UrlImage[]);
      setBoxContents(product.box_content);
      setKeySellingPoints(product.key_selling_points);
      setDescription(product.description);
      setVideos(product.videos);

      const initializeTranslation = (
        translation: ProductTranslation,
        keySellingPointsCount: number,
        boxContentsCount: number,
        translationsMetadata: { id: number; localeId: number }[],
        nameTranslations: { localeId: number; translation: string }[],
        keySellingPointsTranslations: {
          localeId: number;
          translations: string[];
        }[],
        descriptionTranslations: { localeId: number; translation: string }[],
        boxContentsTranslations: { localeId: number; translations: string[] }[],
      ) => {
        if (translation.locale_id) {
          translationsMetadata.push({
            localeId: translation.locale_id,
            id: translation.id,
          });
          nameTranslations.push({
            localeId: translation.locale_id,
            translation: translation.name || '',
          });
          descriptionTranslations.push({
            localeId: translation.locale_id,
            translation: translation.description || '',
          });
          const kspTranslation = {
            localeId: translation.locale_id,
            translations: fillArrayEnd(translation.key_selling_points, keySellingPointsCount),
          };
          keySellingPointsTranslations.push(kspTranslation);
          const boxTranslation = {
            localeId: translation.locale_id,
            translations: fillArrayEnd(translation.box_content, boxContentsCount),
          };
          boxContentsTranslations.push(boxTranslation);
        }
      };

      const translationsMetadata: { id: number; localeId: number }[] = [];
      const nameTranslations: { localeId: number; translation: string }[] = [];
      const keySellingPointsTranslations: {
        localeId: number;
        translations: string[];
      }[] = [];
      const descriptionTranslations: {
        localeId: number;
        translation: string;
      }[] = [];
      const boxContentsTranslations: {
        localeId: number;
        translations: string[];
      }[] = [];

      locales.forEach(locale => {
        const translation = product.translations?.find(t => t.locale_id === locale.id) || getInitialProductTranslation(locale.id);
        initializeTranslation(
          translation,
          product.key_selling_points.length,
          product.box_content.length,
          translationsMetadata,
          nameTranslations,
          keySellingPointsTranslations,
          descriptionTranslations,
          boxContentsTranslations,
        );
      });

      setTranslationsMetadata(translationsMetadata);
      setNameTranslations(nameTranslations);
      setKeySellingPointsTranslations(keySellingPointsTranslations);
      setDescriptionTranslations(descriptionTranslations);
      setBoxContentsTranslations(boxContentsTranslations);

      const specsWithInitializedTranslations = (product.main_specs || []).map(spec => {
        locales.forEach(locale => {
          const existingTranslationIndex = (spec.translations || []).findIndex(t => t.locale_id === locale.id);
          if (existingTranslationIndex !== -1 && spec.translations) {
            spec.translations[existingTranslationIndex].lines = fillArrayEnd(spec.translations[existingTranslationIndex].lines, spec.lines.length);
          } else {
            spec.translations = [...(spec.translations || []), getInitialSpecTranslation(locale.id, spec.lines.length)];
          }
        });
        return spec;
      });
      setSpecs(specsWithInitializedTranslations);
    },
    [locales],
  );

  useEffect(() => {
    async function fetchProductAndProductRelatedData() {
      try {
        dispatch(fetchAdminCategories());
        const tagsResponse = await productServiceAdminTagsClient.getTags();
        setAllTagsInSystem(tagsResponse.data.tags);
        const propertiesResponse = await productServicePropertiesClient.getProperties();
        setAllPropertiesInSystem(propertiesResponse.data.properties);
        if (productId) {
          const response = await productServiceAdminProductsClient.getAdminProduct(productId);
          initializeProductDataFromEnvelope(response.data);
          setVariationsExist((response.data.product.variants || []).length > 1);
          setAddMode(false);
        } else {
          setPopulateMode(true);
          setAddMode(true);
        }
      } catch {
        dispatch(
          setNotification({
            text: t('errors.productInitialFetchError'),
            show: true,
            type: 'warning',
          }),
        );
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    fetchProductAndProductRelatedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const render = (): JSX.Element => {
    let content;

    if (loading) {
      content = (
        <Div alignTextToCenter padding={{ top: 'xlg' }}>
          <Loader size="lg" />
        </Div>
      );
    } else if (error) {
      content = (
        <Div alignTextToCenter padding={{ top: 'xlg' }}>
          <Title>{t('errors.productInitialFetchError')}</Title>
        </Div>
      );
    } else if (locales.length === 0 || allCategoriesInSystem.length === 0) {
      content = (
        <Div alignTextToCenter padding={{ top: 'xlg' }}>
          <Title>{t('errors.dependencyDataError')}</Title>
        </Div>
      );
    } else if (populateMode) {
      content = (
        <ProductPopulate
          initializeProductDataFromEnvelope={initializeProductDataFromEnvelope}
          locales={locales}
          variationsExist={variationsExist}
          setVariationsExist={setVariationsExist}
          setPopulateMode={setPopulateMode}
        />
      );
    } else {
      content = (
        <ProductEdit
          id={id}
          brand={brand}
          handleBrandChange={handleBrandChange}
          type={type}
          handleTypeChange={handleTypeChange}
          name={name}
          handleNameChange={handleNameChange}
          nameTranslations={nameTranslations}
          handleNameTranslationsChange={handleNameTranslationsChange}
          handleVariantsTeliaFinanceGUIDChange={handleVariantsTeliaFinanceGUIDChange}
          variants={variants}
          handleVariantsChange={handleVariantsChange}
          subCategories={subCategories}
          handleSubCategoriesChange={handleSubCategoriesChange}
          tags={tags}
          handleTagsChange={handleTagsChange}
          images={images}
          handleImagesChange={handleImagesChange}
          translationsMetadata={translationsMetadata}
          keySellingPoints={keySellingPoints}
          handleKeySellingPointsChange={handleKeySellingPointsChange}
          keySellingPointsTranslations={keySellingPointsTranslations}
          handleKeySellingPointsTranslationsChange={handleKeySellingPointsTranslationsChange}
          description={description}
          handleDescriptionChange={handleDescriptionChange}
          descriptionTranslations={descriptionTranslations}
          handleDescriptionTranslationsChange={handleDescriptionTranslationsChange}
          boxContents={boxContents}
          handleBoxContentsChange={handleBoxContentsChange}
          boxContentsTranslations={boxContentsTranslations}
          handleBoxContentsTranslationsChange={handleBoxContentsTranslationsChange}
          specs={specs}
          handleSpecsChange={handleSpecsChange}
          videos={videos}
          handleVideosChange={handleVideosChange}
          properties={properties}
          propertyValues={propertyValues}
          handleAddProperty={handleAddProperty}
          handleRemoveProperty={handleRemoveProperty}
          allTagsInSystem={allTagsInSystem}
          allPropertiesInSystem={allPropertiesInSystem}
          allCategoriesInSystem={allCategoriesInSystem}
          currentLocale={currentLocale}
          locales={locales}
          handleLocaleChange={handleLocaleChange}
          variationsExist={variationsExist}
          addMode={addMode}
          countries={countries}
        />
      );
    }

    return <Div margin={{ vertical: 'lg', horizontal: 'xxlg' }}>{content}</Div>;
  };

  return render();
}
